import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

import { ProjectService } from '../../../../../common/services/project.service';
import { Guid } from 'guid-typescript';
import { CreateProject, Project, UpdateProject } from '../../../../../common/models/project';
import { FormattedNumberInputDirective } from '../../../../../common/directives/inputs/formatted-number.directive';
import { MatCardModule } from '@angular/material/card';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-project',
    templateUrl: './project.component.html',
    styleUrls: ['./project.component.scss'],
    imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    ReactiveFormsModule,
    FormattedNumberInputDirective
]
})
export class ProjectComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  projectForm: FormGroup;
  submissionId: string | null = null;
  projectId: Guid | null = null;

  isUpdating: boolean = false;

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(
    private fb: FormBuilder,
    private projectService: ProjectService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute
  ) {

    this.projectForm = this.fb.group({
      name: [null],
      approvedAmount: [null, Validators.required],
    });

  }

  ngOnInit(): void {
    const state = window.history.state as { programProjectName?: string };
    if (state?.programProjectName) {
      this.projectForm.patchValue({
        name: state.programProjectName,
      });
    }
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.submissionId = params.get('submissionId');
      const projectIdparam = params.get('projectId');
      if (projectIdparam) {
        this.isUpdating = true;
        this.projectId = Guid.parse(projectIdparam);
        this.loadProjectData(this.projectId);
      } else {
        this.isUpdating = false;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjectData(projectId: Guid): void {
    this.projectService.getProject(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (project: Project) => {
        this.projectForm.patchValue({
          name: project.name,
          approvedAmount: project.approvedAmount ?? 0
        });
      },
      error: () => {
        this.snackBar.open('Greška prilikom učitavanja projekta.', 'Zatvori', this.snackbarConfig);
      }
    });
  }

  onSubmit(): void {
    if (this.isUpdating) {
      this.updateProject();
    } else {
      this.createProject();
    }
  }

  createProject() {
    const createData: CreateProject = {
      submissionId: this.submissionId,
      ...this.projectForm.value
    }

    this.projectService.createProject(createData).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Projekt uspješno kreiran.', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['/municipality/projects']);
      }
    })
  }

  updateProject() {
    const updatedData: UpdateProject = {
      ...this.projectForm.value,
    };

    this.projectService.updateProject(this.projectId!, updatedData).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Projekt uspješno ažuriran.', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['/municipality/projects']);
      },
      error: () => {
        this.snackBar.open('Greška prilikom ažuriranja projekta. Pokušajte ponovno.', 'Zatvori', this.snackbarConfig);
      }
    });
  }
}